import { Link } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';
import { motion } from 'framer-motion';
import { Calendar, ArrowLeft, Loader2 } from 'lucide-react';
import Header from '@/components/Header';
import Footer from '@/components/Footer';
import { apiEndpoints, apiRequest, resolveApiAssetUrl } from '@/config/api';
import { BlogPost, ApiResponse } from '@/types/api';
import SeoHead, { companySchema } from '@/components/SeoHead';

const Blog = () => {
  const { data, isLoading } = useQuery({
    queryKey: ['blog-list'],
    queryFn: () => apiRequest<ApiResponse<BlogPost[]>>(apiEndpoints.blogs),
  });

  const posts = data?.data || [];

  return (
    <div className="min-h-screen bg-background" dir="rtl">
      <SeoHead
        title="مقالات و اخبار الکتروموتور | کیا الکتروموتور پارت"
        description="مقالات تخصصی، اخبار و راهنمای انتخاب و استفاده از الکتروموتورهای صنعتی و فن‌های کیا الکتروموتور پارت."
        canonicalPath="/blog"
        keywords={['مقالات الکتروموتور', 'راهنمای الکتروموتور صنعتی', 'اخبار الکتروموتور', 'کیا الکتروموتور']}
        structuredData={{ '@context': 'https://schema.org', '@type': 'CollectionPage', name: 'مقالات و اخبار الکتروموتور', about: companySchema, url: 'https://kiaelectromotor.com/blog' }}
      />
      <Header />
      <main className="pt-14 sm:pt-16 md:pt-20">
        <section className="py-16 bg-gradient-to-b from-steel-dark to-steel text-white text-center">
          <h1 className="text-4xl md:text-5xl font-bold">مقالات و اخبار</h1>
          <p className="text-white/80 mt-3">آخرین مطالب تخصصی الکترو موتورهای الکتریکی</p>
        </section>

        <section className="py-16 container mx-auto px-4">
          {isLoading ? (
            <div className="flex justify-center py-20"><Loader2 className="w-10 h-10 animate-spin text-copper" /></div>
          ) : posts.length === 0 ? (
            <p className="text-center text-muted-foreground py-20">هنوز مقاله‌ای منتشر نشده است.</p>
          ) : (
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
              {posts.map((p, i) => (
                <motion.article key={p.id} initial={{ opacity: 0, y: 20 }} whileInView={{ opacity: 1, y: 0 }} transition={{ delay: i * 0.05 }} viewport={{ once: true }} className="bg-card rounded-xl border border-border shadow-lg overflow-hidden hover:shadow-2xl transition-shadow group">
                  <Link to={`/blog/${p.slug}`}>
                    <div className="h-48 bg-gradient-to-br from-copper/20 to-steel/20 overflow-hidden">
                      {p.featured_image ? (
                        <img src={resolveApiAssetUrl(p.featured_image) || ''} alt={p.title} width={600} height={400} className="w-full h-full object-cover group-hover:scale-105 transition-transform" loading="lazy" decoding="async" />
                      ) : (
                        <div className="w-full h-full flex items-center justify-center text-5xl text-copper/50">⚡</div>
                      )}
                    </div>
                    <div className="p-6">
                      <div className="flex items-center text-xs text-muted-foreground mb-2"><Calendar className="w-3 h-3 ml-1" />{new Date(p.publish_date || p.created_at).toLocaleDateString('fa-IR')}</div>
                      <h2 className="text-lg font-bold text-steel-dark mb-2 group-hover:text-copper line-clamp-2">{p.title}</h2>
                      <p className="text-muted-foreground text-sm line-clamp-3 mb-3">{p.excerpt}</p>
                      <span className="inline-flex items-center text-copper font-medium text-sm">ادامه مطلب <ArrowLeft className="w-4 h-4 mr-1" /></span>
                    </div>
                  </Link>
                </motion.article>
              ))}
            </div>
          )}
        </section>
      </main>
      <Footer />
    </div>
  );
};

export default Blog;
